import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LayoutDashboard, Building2, FolderKanban, Coins, Settings, Stethoscope, LogOut, TerminalSquare, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/saas/AuthContext";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/customers", label: "Customers", icon: Building2 },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban },
  { to: "/admin/credits", label: "Credits", icon: Coins },
  { to: "/admin/messages", label: "Messages", icon: Mail },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/admin/diagnostics", label: "Diagnostics", icon: Stethoscope },
];

export default function AdminLayout() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-re-soft">
      <Toaster position="top-center" richColors />
      <div className="flex">
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-re-line bg-white px-4 py-5 md:flex" data-testid="admin-sidebar">
          <div className="px-2"><Logo variant="light" emblemY={7} /></div>
          <p className="mt-1 px-2 text-xs font-semibold uppercase tracking-wider text-re-red">Owner console</p>
          <nav className="mt-6 flex-1 space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-re-red/10 text-re-red" : "text-re-charcoal hover:bg-re-soft"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
            <a href="/prototype" data-testid="admin-nav-prototype" className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-re-muted transition-colors hover:bg-re-soft">
              <TerminalSquare className="h-4 w-4" /> Legacy tooling
            </a>
          </nav>
          <Button variant="outline" onClick={doLogout} data-testid="admin-logout" className="rounded-full"><LogOut className="mr-1.5 h-4 w-4" /> Log out</Button>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="flex items-center justify-between gap-2 border-b border-re-line bg-white px-4 py-3 md:hidden">
            <Logo variant="light" emblemY={7} />
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="admin-logout-mobile" className="rounded-full"><LogOut className="h-4 w-4" /></Button>
          </header>
          <nav className="flex gap-1 overflow-x-auto border-b border-re-line bg-white px-3 py-2 md:hidden">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-mnav-${n.label.toLowerCase()}`} className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${isActive ? "bg-re-red text-white" : "text-re-muted"}`}>{n.label}</NavLink>
            ))}
          </nav>
          <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6"><Outlet /></main>
        </div>
      </div>
    </div>
  );
}
